import React, { useContext } from 'react';

import { media } from 'context';
import { Button, Layout } from 'ds';
import { RequestState } from 'ds/types';
import { apiTrack } from 'lib/analytics';
import { actions } from 'store/ServicesRequest';
import { selectServicesRequestCompletedSteps } from 'store/ServicesRequest/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { MOBILE_PROGRESS_FOOTER_HEIGHT } from './constants';
import { ServicesRequestStep } from './types';

interface Props {
  step: ServicesRequestStep;
  nextHref?: string;
  ctaDisabled?: boolean;
  skippable?: boolean;
  backHref?: string;
  onSubmit?: VoidFunction;
  requestState?: RequestState;
  stepCompletedEventValue?: string | number | string[] | number[] | object | null;
  stepCompletedEventValues?: object;
  stepName: string;
}

const ProgressFooter: React.FC<Props> = ({
  step,
  nextHref,
  ctaDisabled,
  skippable,
  backHref,
  onSubmit,
  requestState,
  stepCompletedEventValue,
  stepCompletedEventValues,
  stepName
}) => {
  const requestInProgress = requestState === 'in_progress';
  const { xs, sm } = useContext(media);
  const isMobile = xs || sm;
  const completedSteps = useAppSelector(selectServicesRequestCompletedSteps);
  const dispatch = useAppDispatch();

  const trackStep = ({ skipped }: { skipped: boolean }) => {
    if (!completedSteps.includes(step)) {
      apiTrack('Services Request Step Completed', {
        step,
        position: completedSteps.length + 1,
        value: stepCompletedEventValue || '',
        ...stepCompletedEventValues,
        name: stepName,
        ...(skipped ? { skipped: true } : {})
      });
      dispatch(actions.addStepComplete(step));
    }
  };

  const progressPercentage =
    step === 'services' ? 0 : step === 'location' ? 20 : step === 'size' ? 40 : step === 'move' ? 60 : 80;

  const content = (
    <Layout justify="space-between" align="center" width="100%">
      <Layout>
        {backHref && (
          <Button
            data-cy="back-button"
            size="sm"
            type="secondary"
            text="Back"
            href={backHref}
            disabled={requestInProgress}
          />
        )}
      </Layout>
      <Layout gap={12}>
        {skippable && (
          <Button
            data-cy="skip-button"
            size="sm"
            type="secondary"
            text="Skip"
            href={nextHref}
            onClick={() => trackStep({ skipped: true })}
          />
        )}
        {onSubmit ? (
          <Button
            data-cy="submit-button"
            type="primary"
            text={requestInProgress ? 'Submitting...' : 'Submit'}
            disabled={ctaDisabled || requestInProgress}
            onClick={() => {
              trackStep({ skipped: false });
              onSubmit();
            }}
            size="sm"
          />
        ) : (
          nextHref && (
            <Button
              data-cy="next-button"
              size="sm"
              type="primary"
              text="Next"
              disabled={ctaDisabled}
              href={nextHref}
              onClick={() => trackStep({ skipped: false })}
            />
          )
        )}
      </Layout>
    </Layout>
  );

  return isMobile ? (
    <Layout
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      height={MOBILE_PROGRESS_FOOTER_HEIGHT}
      background="white"
      __style={{
        boxShadow: '0px -4px 8px rgba(0, 0, 0, 0.08)'
      }}
    >
      <Layout height={2} width={`${progressPercentage}%`} color="blue-500" transition="width 0.3s ease-in-out" />
      <Layout width="100%" align="center" paddingX={24} paddingTop={12}>
        {content}
      </Layout>
    </Layout>
  ) : (
    <Layout>
      <Layout
        marginTop={16}
        height={2}
        width={`${progressPercentage}%`}
        color="blue-500"
        transition="width 0.3s ease-in-out"
      />
      <Layout
        direction="column"
        paddingX={40}
        paddingY={24}
        __style={{
          borderTop: '1px solid var(--color-gray-100)'
        }}
      >
        {content}
      </Layout>
    </Layout>
  );
};

export default ProgressFooter;
